import { StatusBadge } from '../../../../components/base/StatusBadge';
import { formatCurrencyCompact, formatDate, formatNumber } from '../../../../utils/format';
import type { Harvest } from '../../../../types';

interface HarvestCardProps {
  harvest: Harvest;
  farmerName: string;
  seasonName: string;
  fieldName: string;
  onViewDetail: () => void;
  onConfirm: () => void;
}

export default function HarvestCard({
  harvest, farmerName, seasonName, fieldName,
  onViewDetail, onConfirm,
}: HarvestCardProps) {
  // Compare against this harvest's own expected quantity, not the season's total:
  // both live on the same record so they are guaranteed to share `donVi`, and it
  // is the same figure the backend uses to raise the "Sản lượng thấp" warning.
  // (The season's expectedYield carries its own unit — kg, tấn, tạ, whatever the
  // farmer picked — so subtracting it here produced nonsense like -5.494.981 kg.)
  const duKien = harvest.soLuong || 0;
  const thucTe = harvest.actualYieldConfirmed || harvest.actualYield || 0;
  const chenhLech = thucTe - duKien;
  const showConfirm = harvest.status === 'Nông hộ đã cập nhật' || harvest.status === 'Chờ HTX xác nhận';

  const getCanhBaoColor = (canhBao?: string) => {
    if (!canhBao) return '';
    if (canhBao === 'Sản lượng thấp') return 'bg-red-50 text-red-700 border-red-200';
    if (canhBao === 'Thu hoạch trễ') return 'bg-orange-50 text-orange-700 border-orange-200';
    if (canhBao === 'Mùa vụ có nguy cơ giảm sản lượng') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (canhBao === 'Ảnh hưởng đến hợp đồng') return 'bg-sky-50 text-sky-700 border-sky-200';
    return 'bg-amber-50 text-amber-700 border-amber-200';
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-900">{harvest.maLanThuHoach}</p>
            <StatusBadge status={harvest.status} />
            {harvest.canhBao && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${getCanhBaoColor(harvest.canhBao)}`}>
                <span className="w-3.5 h-3.5 flex items-center justify-center mr-1">
                  <i className="ri-alert-line" />
                </span>
                {harvest.canhBao}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {farmerName} · {seasonName} · {fieldName}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-1 mt-2">
            <div>
              <p className="text-xs text-slate-400">Cây trồng</p>
              <p className="text-xs font-medium text-slate-700">{harvest.cayTrong}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Ngày dự kiến</p>
              <p className="text-xs font-medium text-slate-700">{harvest.ngayDuKien ? formatDate(harvest.ngayDuKien) : '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Ngày thực tế</p>
              <p className="text-xs font-medium text-slate-700">{harvest.ngayThuHoach ? formatDate(harvest.ngayThuHoach) : '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Sản lượng dự kiến</p>
              <p className="text-xs font-medium text-slate-700">{harvest.soLuong ? formatNumber(harvest.soLuong) : '-'} {harvest.donVi}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Sản lượng nông hộ</p>
              <p className="text-xs font-medium text-slate-700">
                {harvest.actualYield ? `${formatNumber(harvest.actualYield)} ${harvest.donVi}` : 'Chưa cập nhật'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Sản lượng HTX xác nhận</p>
              <p className="text-xs font-medium text-slate-700">
                {harvest.actualYieldConfirmed ? `${formatNumber(harvest.actualYieldConfirmed)} ${harvest.donVi}` : 'Chờ xác nhận'}
              </p>
            </div>
          </div>
          {harvest.ngayDuKien && harvest.ngayThuHoach && (
            <div className="mt-1">
              <p className="text-xs text-slate-400">
                Chênh lệch:
                <span className={`font-medium ml-1 ${chenhLech >= 0 ? 'text-agrida-700' : 'text-red-600'}`}>
                  {chenhLech >= 0 ? '+' : ''}{formatNumber(chenhLech)} {harvest.donVi}
                </span>
                {' '}({duKien > 0 ? `${((thucTe / duKien) * 100).toFixed(1)}%` : '-'})
              </p>
            </div>
          )}
          {harvest.chatLuong && (
            <p className="text-xs text-slate-500 mt-1">Chất lượng: {harvest.chatLuong}</p>
          )}
          {harvest.ghiChuChatLuong && (
            <p className="text-xs text-slate-500">Ghi chú chất lượng: {harvest.ghiChuChatLuong}</p>
          )}
          {harvest.giaUocTinh > 0 && (
            <p className="text-xs text-slate-500">Giá ước tính: {formatCurrencyCompact(harvest.giaUocTinh)}/{harvest.donVi}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
        <button
          type="button"
          onClick={onViewDetail}
          className="text-sm text-agrida-700 hover:text-agrida-800 font-medium flex items-center gap-1 min-h-[44px]"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line" /></span>
          Xem chi tiết
        </button>
        {showConfirm && (
          <button
            type="button"
            onClick={onConfirm}
            className="text-sm bg-agrida-700 hover:bg-agrida-800 text-white px-3 py-2 rounded-md font-medium transition-colors whitespace-nowrap min-h-[44px]"
          >
            Xác nhận
          </button>
        )}
      </div>
    </div>
  );
}